/**
 * TodoCreateContainer — CD-25b: creating a to-do wears the SAME container as
 * viewing one.
 *
 * CD-25 rebuilt the *view* modal onto `DetailModalShell`'s compact preset and
 * left every CREATE surface on the pre-CD-20 chrome, so "+ todo" from a card
 * and "+ New Todo" on /todos still looked like the old app. This is that half:
 * identical shell, identical bands (HEAD · META · SOURCE · BODY · FOOTER),
 * identical menu grammar. The only differences a create form is entitled to are
 * that the title is an empty input and the footer says Cancel / Create.
 *
 * Notably the type selector is the META-row **picker**, not the old row of nine
 * chips — the handoff has exactly one Type control and this is it, so the two
 * modes can't drift apart again.
 *
 * The caller owns the WRITE. Each entry point has its own data layer (the
 * sandbox data source, `todosApi.create` with a card's source pair, EOS L10's
 * `createFn` override), so this component collects a draft and hands it over;
 * it never picks an endpoint. That is what lets one component serve every
 * create surface without knowing about any of them.
 *
 * The to-dos upgrade (Claude Design handoff, 2026-10-04) — the full create
 * modal for anything the quick-add line cannot say in one line:
 *   • META: Type · Due (one week out, visibly "suggested" until someone picks)
 *     · Assignee (the person creating it) · Team · Repeat (a short preset list)
 *   • SOURCE: client › project, deal or ticket — and `prefill` carries the page
 *     a to-do was made from (Michael: "depending on where this todo is coming
 *     from, it'll auto populate the information — like client, project,
 *     contact, team")
 *   • WHO IT GOES TO for an email / text / call / booking: the client's
 *     contacts; a contact with no phone number keeps the verb off and says so
 *   • footer: "Lands in Oct 11 (suggested)" — or what is missing, with "you can
 *     still create it" — and "+ Create to-do"
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DetailModalShell from '../shared/UnifiedCardDetailModal/DetailModalShell';
import tc from './todoContainer.module.css';
import TodoTypeIcon, { todoTypeTileClass } from './TodoTypeIcon';
import TodoMenu from './TodoMenu';
import TodoSourceRow, { type TodoSourceClient, type TodoSourceRecord, type TodoLinkPick } from './TodoSourceRow';
import { TodoCallCell } from './TodoCallCell';
import { TODO_TYPES, todoTypeDef, type TodoType } from '../shared/todoTypes';
import { callLinkFloorLabel, type CallLink } from './useCallLinks';
import { defaultTodoDueDate, todoDueLabel } from './todoDue';
import { repeatPresetsFor, type RepeatPreset, type RepeatRule } from './todoRepeat';
import type { AssigneeOption } from '../shared/InlineEdit/types';
import { cachedTodoClients, loadTodoClientsOnce } from './todoClients';
import { todosApi, type TodoLinkTargets, type TodoTeam } from '../../services/todosApi';
import { TEAMS, TEAM_LABEL } from './hub/todoModel';
import { TodoDueChip, TodoPickerChip, TodoAvatar, RepeatGlyph } from './hub/TodoBits';

/** The lists a to-do can be filed under. Mirrors the view container's List cell. */
export const TODO_LISTS = ['sales', 'operations', 'marketing', 'eos', 'standalone'] as const;
export type TodoListName = (typeof TODO_LISTS)[number];

/** A record a new to-do hangs off: picked in the SOURCE band, or the page it
 *  was made from (GET /api/todos/context). */
export interface TodoCreateRecord {
  kind: 'project' | 'deal' | 'ticket' | 'eos_l10' | 'eos_issue' | 'eos_rock';
  id: string;
  label: string;
  source_type: string;
  source_id: string;
  ticket_id?: number | null;
}

export interface TodoCreateContact { id: number; name: string; email: string | null; phone: string | null }

/** What a create starts with — the quick-add line's seed, or the page context. */
export interface TodoCreatePrefill {
  title?: string;
  task_type?: TodoType;
  due_date?: string | null;
  due_suggested?: boolean;
  assignee_id?: number | null;
  team?: TodoTeam | null;
  repeat?: RepeatPreset | null;
  client_card_id?: string | null;
  client_name?: string | null;
  record?: TodoCreateRecord | null;
  contact?: TodoCreateContact | null;
  instructions?: string;
  /** EOS L10 team ('ops', 'sales-marketing', …) when made from a meeting. */
  department?: string | null;
}

export interface TodoCreateDraft {
  title: string;
  task_type: TodoType;
  due_date: string | null;
  assignee_id: number | null;
  list: TodoListName;
  instructions: string;
  client_card_id: string | null;
  /** Set when the user linked a project in the SOURCE row before creating.
   *  (`record` carries the same pick, and deals / tickets too.) */
  project_id: number | null;
  /** The call this to-do books (`scheduling_event_templates.id`). */
  event_template_id: number | null;
  /** The exact person's link it books on (`scheduling_event_types.id`) — the
   *  call-link picker's pick; bind with `todosApi.setCallType(id, tpl, link)`. */
  event_type_id: number | null;
  /** The to-dos upgrade (2026-10-04). */
  team: TodoTeam | null;
  /** False = nobody chose and nothing implied one: leave it to the server's
   *  source default (a marketing card's to-do lands on Marketing). */
  team_explicit: boolean;
  /** True while the date is still the suggestion nobody confirmed. */
  due_suggested: boolean;
  repeat: RepeatRule | null;
  /** Who an email / text / call goes to — wins over the record's own contact. */
  contact_id: number | null;
  /** The record picked or carried in (project / deal / ticket / EOS). */
  record: TodoCreateRecord | null;
  department: string | null;
}

export interface TodoCreateContainerProps {
  open: boolean;
  onClose: () => void;
  /** Perform the write. Throw to surface an error in the footer. */
  onCreate: (draft: TodoCreateDraft) => Promise<void>;
  assigneeOptions: AssigneeOption[];
  currentUserId?: number | null;
  /** Active clients for the SOURCE row picker. Omit and the container loads the
   *  roster itself — a card modal only knows the client's card_id, so without
   *  it a pre-attached client rendered as the literal word "Client". */
  clients?: TodoSourceClient[];
  /** Engine-only affordances. The localStorage sandbox has neither call types
   *  nor projects, so both default off and the cells simply don't render. */
  supportsCallTypes?: boolean;
  supportsProjectLink?: boolean;
  /** Pre-attach a client (the client file's "+ Client To-Do"). */
  prefilledClientCardId?: string | null;
  /** The record this to-do is being created ON (a card modal's "+ todo"). Shown
   *  in the SOURCE row as provenance; not editable, because the caller already
   *  decided it. */
  fixedRecord?: TodoSourceRecord | null;
  /** Default list/department. */
  defaultList?: TodoListName;
  /** Optional title for the shell's aria-label. */
  ariaLabel?: string;
  /** Everything a create starts with (the quick-add seed, the page it came from). */
  prefill?: TodoCreatePrefill | null;
  /** The parent opens the action's composer right after the create (the card
   *  modal's chain), so the button promises it: "Compose email →". Without it
   *  the button is the design's "+ Create to-do". */
  chainVerb?: boolean;
}

const COMMS: ReadonlySet<string> = new Set(['email', 'text', 'phone', 'schedule_call']);
const RECORD_KIND_LABEL: Record<TodoCreateRecord['kind'], TodoSourceRecord['kind']> = {
  project: 'PROJECT', deal: 'DEAL', ticket: 'TICKET', eos_l10: 'EOS', eos_issue: 'EOS', eos_rock: 'EOS',
};
const TEAM_FOR_RECORD: Partial<Record<TodoCreateRecord['kind'], TodoTeam>> = { deal: 'sales', project: 'ops', ticket: 'ops' };
const TEAM_FOR_FIXED: Partial<Record<TodoSourceRecord['kind'], TodoTeam>> = { DEAL: 'sales', PROJECT: 'ops', TICKET: 'ops' };

export function recordFromPick(pick: TodoLinkPick): TodoCreateRecord {
  if (pick.kind === 'ticket') {
    return { kind: 'ticket', id: String(pick.id), label: pick.label, source_type: 'ticket', source_id: `ticket:${pick.id}`, ticket_id: pick.id };
  }
  return { kind: pick.kind, id: String(pick.id), label: pick.label, source_type: pick.kind, source_id: String(pick.id) };
}

const firstName = (n: string) => n.trim().split(/\s+/)[0] || n;

const TodoCreateContainer: React.FC<TodoCreateContainerProps> = ({
  open,
  onClose,
  onCreate,
  assigneeOptions,
  currentUserId,
  clients = [],
  supportsCallTypes = false,
  supportsProjectLink = false,
  prefilledClientCardId,
  fixedRecord = null,
  defaultList = 'standalone',
  ariaLabel = 'Create todo',
  prefill = null,
  chainVerb = false,
}) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<TodoType>('todo');
  // A new to-do starts due ONE WEEK OUT — visible and editable here, never
  // silent (Michael, 2026-10-03: "a suggested default due date a week out
  // just to ensure if someone forgets to add a due date there's one there").
  const [due, setDue] = useState<string | null>(() => defaultTodoDueDate());
  const [suggested, setSuggested] = useState(true);
  const [assigneeId, setAssigneeId] = useState<number | null>(currentUserId ?? null);
  const [team, setTeam] = useState<TodoTeam | null>(null);
  const teamTouched = useRef(false);
  const [repeat, setRepeat] = useState<RepeatPreset | null>(null);
  const [list] = useState<TodoListName>(defaultList);
  const [instructions, setInstructions] = useState('');
  const [clientCardId, setClientCardId] = useState<string | null>(prefilledClientCardId ?? null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [record, setRecord] = useState<TodoCreateRecord | null>(null);
  const [contact, setContact] = useState<TodoCreateContact | null>(null);
  const [callLink, setCallLink] = useState<CallLink | null>(null);

  const [typeOpen, setTypeOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedClients, setLoadedClients] = useState<TodoSourceClient[]>(
    () => cachedTodoClients().map((c) => ({ card_id: c.card_id, title: c.title })),
  );
  const [targets, setTargets] = useState<TodoLinkTargets | null>(null);

  const titleRef = useRef<HTMLInputElement | null>(null);
  const typeAnchor = useRef<HTMLButtonElement | null>(null);
  const contactAnchor = useRef<HTMLButtonElement | null>(null);

  const isScheduling = type === 'schedule_call';

  // Reset on open so a previous draft can't bleed into the next create; the
  // prefill (a quick-add seed, the page it was made from) is the new start.
  useEffect(() => {
    if (!open) return;
    const p = prefill ?? {};
    setTitle(p.title ?? '');
    setType(p.task_type ?? 'todo');
    if (p.due_date !== undefined) { setDue(p.due_date); setSuggested(!!p.due_date && p.due_suggested !== false); }
    else { setDue(defaultTodoDueDate()); setSuggested(true); }
    setAssigneeId(p.assignee_id !== undefined ? p.assignee_id : (currentUserId ?? null));
    const startRecord = p.record ?? null;
    const startTeam = p.team !== undefined
      ? p.team
      : startRecord
        ? TEAM_FOR_RECORD[startRecord.kind] ?? null
        : fixedRecord ? TEAM_FOR_FIXED[fixedRecord.kind] ?? null : null;
    setTeam(startTeam);
    teamTouched.current = p.team !== undefined;
    setRepeat(p.repeat ?? null);
    setInstructions(p.instructions ?? '');
    setClientCardId(p.client_card_id ?? prefilledClientCardId ?? null);
    setClientName(p.client_name ?? null);
    setRecord(startRecord);
    setContact(p.contact ?? null);
    setCallLink(null);
    setTypeOpen(false); setCallOpen(false); setContactOpen(false);
    setSubmitting(false); setError(null);
    // `prefill` is read at open; later edits are the person's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentUserId, defaultList, prefilledClientCardId]);

  useEffect(() => { if (open) titleRef.current?.focus(); }, [open]);

  // Load the roster when the caller didn't supply one (the card-modal path).
  useEffect(() => {
    if (!open || clients.length || loadedClients.length) return;
    let alive = true;
    void loadTodoClientsOnce().then((rows) => {
      if (alive) setLoadedClients(rows.map((c) => ({ card_id: c.card_id, title: c.title })));
    });
    return () => { alive = false; };
  }, [open, clients.length, loadedClients.length]);

  const clientOptions = clients.length ? clients : loadedClients;

  const needsContact = COMMS.has(type);
  // The client's contacts, for "who it goes to" — read only when it matters.
  useEffect(() => {
    if (!open || !clientCardId || !needsContact || !supportsProjectLink) { setTargets(null); return; }
    let alive = true;
    todosApi.linkTargets(clientCardId)
      .then((r) => { if (alive) setTargets(r.data ?? null); })
      .catch(() => { if (alive) setTargets(null); });
    return () => { alive = false; };
  }, [open, clientCardId, needsContact, supportsProjectLink]);

  // Leaving schedule_call drops a half-picked call so a to-do can't be created
  // as an email carrying a stale delivery-call binding.
  useEffect(() => { if (!isScheduling) { setCallLink(null); setCallOpen(false); } }, [isScheduling]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !submitting) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, submitting]);

  const typeDef = todoTypeDef(type);
  const canCreate = title.trim().length > 0 && !submitting;
  const presets = useMemo(() => repeatPresetsFor(due), [due]);

  const selectedCall = callLink
    ? { name: callLink.name, floor_field_label: callLinkFloorLabel(callLink) }
    : null;

  // What would keep the verb off — said in the footer, never a blocker.
  const needsRecord = type === 'create_proposal' || type === 'schedule_build';
  const wantKind = type === 'create_proposal' ? 'deal' : 'project';
  const hasFixedRecord = !!fixedRecord;
  const targetMissing: string | null = (() => {
    if (needsContact) {
      if (contact) {
        if ((type === 'phone' || type === 'text') && !contact.phone) return `No phone number on ${contact.name}`;
        if ((type === 'email' || type === 'schedule_call') && !contact.email) return `No email address on ${contact.name}`;
        return null;
      }
      // Nobody picked is not "missing" — the composer opens and the person
      // types the recipient (the server's rule, todoTargetService).
      return null;
    }
    if (needsRecord && !hasFixedRecord && (!record || record.kind !== wantKind)) return `Link the ${wantKind}`;
    return null;
  })();

  const submit = useCallback(async () => {
    if (!canCreate) return;
    setSubmitting(true);
    setError(null);
    try {
      await onCreate({
        title: title.trim(),
        task_type: type,
        due_date: due || null,
        assignee_id: assigneeId,
        list,
        instructions: instructions.trim(),
        client_card_id: clientCardId,
        project_id: record?.kind === 'project' ? Number(record.id) : null,
        event_template_id: type === 'schedule_call' ? callLink?.event_template_id ?? null : null,
        event_type_id: type === 'schedule_call' ? callLink?.event_type_id ?? null : null,
        team,
        team_explicit: teamTouched.current || team != null,
        due_suggested: !!due && suggested,
        repeat: repeat?.rule ?? null,
        contact_id: needsContact ? contact?.id ?? null : null,
        record,
        department: prefill?.department ?? null,
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to create the to-do');
      setSubmitting(false);
    }
  }, [canCreate, onCreate, title, type, due, assigneeId, list, instructions, clientCardId, record, callLink, team, suggested, repeat, needsContact, contact, prefill]);

  const assignee = assigneeOptions.find((o) => o.id === assigneeId);
  const assigneeLabel = assigneeId == null
    ? 'Unassigned'
    : `${assignee?.name ?? 'Someone'}${currentUserId === assigneeId ? ' (you)' : ''}`;

  // The record shown in the SOURCE row: the caller's fixed record wins (you are
  // creating ON that card); otherwise the record picked here or carried in.
  const sourceRecord: TodoSourceRecord | null = fixedRecord
    ?? (record ? { kind: RECORD_KIND_LABEL[record.kind], label: record.label } : null);

  const pickRecord = (pick: TodoLinkPick) => {
    const next = recordFromPick(pick);
    setRecord(next);
    if (!teamTouched.current) setTeam(TEAM_FOR_RECORD[next.kind] ?? null);
  };

  const contacts = targets?.contacts ?? [];
  const contactMeta = (c: TodoCreateContact) => ((type === 'phone' || type === 'text') ? (c.phone || 'no phone') : (c.email || 'no email'));
  const sectionLabel = type === 'phone' ? 'Who to call' : type === 'schedule_call' ? 'Who to invite' : 'Who it goes to';

  const dueText = due ? todoDueLabel(due, false) : 'Needs a date';
  const hint = !title.trim()
    ? 'Give it a title'
    : targetMissing
      ? `${targetMissing} — you can still create it`
      : `Lands in ${dueText}${due && suggested ? ' (suggested)' : ''}`;

  if (!open) return null;

  return createPortal(
    <DetailModalShell
      preset="compact"
      ariaLabel={ariaLabel}
      onClose={() => { if (!submitting) onClose(); }}
      head={(
        <>
          <span className={`${tc.tile34} ${todoTypeTileClass(type)}`}>
            <TodoTypeIcon type={type} />
          </span>
          <input
            ref={titleRef}
            className={tc.titleInput}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={submitting}
            aria-label="Title"
            placeholder={typeDef.footerLabel ? `${typeDef.label} — what is it about?` : 'What needs doing?'}
            onKeyDown={(e) => { if (e.key === 'Enter' && canCreate) { e.preventDefault(); void submit(); } }}
          />
          <span
            style={{ fontFamily: 'var(--font-head)', fontWeight: 600, fontSize: 10.5, letterSpacing: '.13em', textTransform: 'uppercase', padding: '3px 7px', borderRadius: 3, border: '1px dashed var(--rule-strong)', color: 'var(--ink-faint)', flex: 'none' }}
          >
            New
          </span>
          <button type="button" className={tc.closeBtn} onClick={onClose} aria-label="Close" disabled={submitting}>×</button>
        </>
      )}
      bands={(
        <>
          <div className={tc.metaRow}>
            <div className={tc.metaCell}>
              <div className={tc.metaLabel}>Type</div>
              <button
                ref={typeAnchor}
                type="button"
                className={tc.typeChip}
                onClick={() => setTypeOpen((v) => !v)}
                disabled={submitting}
                aria-haspopup="listbox"
                aria-expanded={typeOpen}
                title="What kind of to-do is this?"
              >
                <span className={`${tc.tile20} ${todoTypeTileClass(type)}`}><TodoTypeIcon type={type} /></span>
                <span className={tc.chipName}>{typeDef.label}</span>
                <span className={tc.chipCaret}>▾</span>
              </button>
              <TodoMenu
                open={typeOpen}
                onClose={() => setTypeOpen(false)}
                anchorRef={typeAnchor}
                header="What kind of to-do"
                width="wide"
                options={TODO_TYPES.map((t) => ({
                  value: t.value,
                  label: t.label,
                  meta: t.functional ? (t.footerLabel || null) : 'soon',
                  disabled: !t.functional,
                  title: t.functional ? undefined : 'Coming soon',
                }))}
                value={type}
                onPick={(v) => { if (v) { setType(String(v) as TodoType); setContact(null); } }}
              />
            </div>

            {isScheduling && supportsCallTypes && (
              <TodoCallCell
                value={callLink?.event_type_id ?? null}
                valueName={callLink ? `${callLink.name} · ${callLink.host_name || 'someone'}` : null}
                onPick={(link) => {
                  setCallLink(link);
                  if (link && !title.trim()) setTitle(`Schedule the ${link.name.toLowerCase()}`);
                }}
                open={callOpen}
                onToggle={() => setCallOpen((v) => !v)}
                onClose={() => setCallOpen(false)}
                disabled={submitting}
              />
            )}

            <div className={tc.metaCell}>
              <div className={tc.metaLabel}>Due</div>
              <TodoDueChip
                todo={{ due, suggested: !!due && suggested, title: title || 'the new to-do' }}
                onPick={(iso) => { setDue(iso); setSuggested(false); }}
                onClear={() => { setDue(null); setSuggested(false); }}
                readOnly={submitting}
              />
            </div>

            <div className={tc.metaCell}>
              <div className={tc.metaLabel}>Assignee</div>
              <TodoPickerChip
                header="Assign to"
                width="wide"
                options={[
                  { value: null, label: 'Unassigned' },
                  ...assigneeOptions.filter((o) => o.id != null).map((o) => ({
                    value: o.id as number,
                    label: `${o.name}${currentUserId === o.id ? ' (you)' : ''}`,
                  })),
                ]}
                value={assigneeId}
                onPick={(v) => setAssigneeId(v == null ? null : Number(v))}
                filterPlaceholder={assigneeOptions.length > 8 ? 'Search people…' : undefined}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {assigneeId != null && <TodoAvatar name={assignee?.name} size={16} />}
                  {assigneeLabel}
                </span>
              </TodoPickerChip>
            </div>

            <div className={tc.metaCell}>
              <div className={tc.metaLabel}>Team</div>
              <TodoPickerChip
                header="Team"
                options={[{ value: null, label: 'No team' }, ...TEAMS.map((k) => ({ value: k, label: TEAM_LABEL[k] }))]}
                value={team}
                onPick={(v) => { teamTouched.current = true; setTeam((v as TodoTeam | null) ?? null); }}
                dashed={!team}
              >
                {team ? TEAM_LABEL[team] : 'Set a team'}
              </TodoPickerChip>
            </div>

            <div className={tc.metaCell}>
              <div className={tc.metaLabel}>Repeat</div>
              <TodoPickerChip
                header="Repeat"
                width="wide"
                options={[{ value: null, label: 'Does not repeat' }, ...presets.map((p) => ({ value: p.key, label: p.label }))]}
                value={repeat?.key ?? null}
                onPick={(v) => setRepeat(v == null ? null : presets.find((p) => p.key === v) ?? null)}
                dashed={!repeat}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  {repeat && <RepeatGlyph size={11} />}
                  {repeat ? repeat.label : 'No repeat'}
                </span>
              </TodoPickerChip>
            </div>
          </div>

          <TodoSourceRow
            clients={clientOptions}
            clientCardId={clientCardId}
            clientName={clientName ?? clientOptions.find((c) => c.card_id === clientCardId)?.title ?? null}
            onPickClient={(id, name) => { setClientCardId(id); setClientName(name); setRecord(null); setContact(null); }}
            record={sourceRecord}
            // A record linked here is undone by re-picking or the ×, not an
            // unlink write — nothing is persisted yet.
            onLinkProject={(id, t) => pickRecord({ kind: 'project', id, label: t })}
            onLinkRecord={supportsProjectLink ? pickRecord : undefined}
            onUnlinkRecord={!fixedRecord && record ? () => setRecord(null) : undefined}
            alsoOfferProjectLink={supportsProjectLink && !!fixedRecord && fixedRecord.kind !== 'PROJECT'}
            disabled={submitting || !supportsProjectLink}
          />
        </>
      )}
      footer={(
        <>
          <div className={tc.footGroup}>
            <button type="button" className={tc.btnGhost} onClick={onClose} disabled={submitting}>Cancel</button>
          </div>
          <span className={tc.footSpacer} />
          <span style={{ fontSize: 11, color: 'var(--ink-faint)', marginRight: 8, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hint}</span>
          <div className={tc.footGroup}>
            <button type="button" className={tc.btnPrimary} onClick={submit} disabled={!canCreate}>
              {submitting
                ? 'Creating…'
                : chainVerb && typeDef.functional && typeDef.footerLabel
                  ? `${typeDef.footerLabel} →`
                  : '+ Create to-do'}
            </button>
          </div>
        </>
      )}
    >
      {/* WHO IT GOES TO — an email / text / call / booking needs a person. The
          client's contacts; a contact with no phone keeps the verb off. */}
      {needsContact && supportsProjectLink && (
        <>
          <div className={tc.sectionHead}>
            <span className={targetMissing ? tc.diamondPaprika : tc.diamondIndigo} />
            <span className={tc.sectionTitle}>{sectionLabel}</span>
          </div>
          <div
            className={tc.ruleCard}
            style={{ display: 'flex', alignItems: 'center', gap: 12, borderStyle: contact ? 'solid' : 'dashed' }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              {contact ? (
                <>
                  <span style={{ display: 'block', fontFamily: 'var(--font-head)', fontWeight: 600, fontSize: 13.5, color: 'var(--ink)' }}>{contact.name}</span>
                  <span style={{ display: 'block', fontSize: 11, marginTop: 2, color: targetMissing ? 'var(--paprika-on-soft)' : 'var(--ink-faint)' }}>
                    {(type === 'phone' || type === 'text')
                      ? (contact.phone || 'No phone number on file — the verb stays off until there is one')
                      : (contact.email || 'No email address on file — the verb stays off until there is one')}
                  </span>
                </>
              ) : (
                <span style={{ fontSize: 12.5, color: 'var(--ink-faint)' }}>
                  {clientCardId
                    ? (record || fixedRecord
                      ? `Goes to the ${(sourceRecord?.kind || 'record').toLowerCase()}'s contact — or pick someone at ${clientName ?? 'the client'}.`
                      : `Pick a contact at ${clientName ?? 'the client'} — or leave it and type the recipient when you send.`)
                    : 'Attach a client below to pick one of its contacts — or leave it and type the recipient when you send.'}
                </span>
              )}
            </span>
            <button
              ref={contactAnchor}
              type="button"
              className={contact ? tc.chip : tc.chipDashed}
              onClick={() => setContactOpen((v) => !v)}
              disabled={submitting || !clientCardId}
              aria-haspopup="listbox"
              aria-expanded={contactOpen}
            >
              {contact ? 'Change' : 'Pick a contact'}
            </button>
            <TodoMenu
              open={contactOpen}
              onClose={() => setContactOpen(false)}
              anchorRef={contactAnchor}
              header={clientName ? `Contacts at ${clientName}` : 'Contacts'}
              width="wide"
              options={[
                ...(contact ? [{ value: null, label: 'Nobody picked' }] : []),
                ...contacts.map((c) => ({ value: c.id, label: c.name, meta: contactMeta(c) })),
              ]}
              value={contact?.id ?? null}
              onPick={(v) => setContact(v == null ? null : contacts.find((c) => c.id === Number(v)) ?? null)}
              filterPlaceholder={contacts.length > 8 ? 'Search contacts…' : undefined}
              emptyLabel={clientCardId ? 'No contacts on this client' : 'Attach a client first'}
            />
          </div>
        </>
      )}

      {/* A proposal reads its deal, a build its project — linked in SOURCE. */}
      {needsRecord && !fixedRecord && (
        <>
          <div className={tc.sectionHead}>
            <span className={record?.kind === wantKind ? tc.diamondIndigo : tc.diamondPaprika} />
            <span className={tc.sectionTitle}>{wantKind === 'deal' ? 'Deal' : 'Project'}</span>
          </div>
          <div className={`${tc.ruleCard} ${record?.kind === wantKind ? '' : tc.ruleCardWarn}`}>
            {record?.kind === wantKind
              ? <><b>{record.label}</b> · {wantKind === 'deal' ? 'Deal' : 'Project'}{clientName ? ` · ${clientName}` : ''}</>
              : `Link the ${wantKind} in the Source band. ${typeDef.footerLabel || 'The action'} stays off until it is linked.`}
          </div>
        </>
      )}

      {/* The consequence of the call choice, stated before it is made — the
          same sentence the view container's Booking rules block will show once
          the to-do exists. */}
      {isScheduling && supportsCallTypes && (
        <>
          <div className={tc.sectionHead}>
            <span className={selectedCall ? tc.diamondIndigo : tc.diamondPaprika} />
            <span className={tc.sectionTitle}>Booking rules</span>
          </div>
          <div className={`${tc.ruleCard} ${selectedCall ? '' : tc.ruleCardWarn}`}>
            {!selectedCall
              ? 'Pick which call this books — without one the composer opens a blank email with no booking link.'
              : selectedCall.floor_field_label
                ? `The ${selectedCall.name} books from a project's ${selectedCall.floor_field_label}. Link a project ${fixedRecord ? 'on the to-do' : 'below'} and the client's earliest slot follows automatically.`
                : `The ${selectedCall.name} has no date rule — the client can book any open slot.`}
          </div>
        </>
      )}

      <div className={tc.sectionHead}>
        <span className={tc.diamondIndigo} />
        <span className={tc.sectionTitle}>Instructions</span>
      </div>
      <div className={tc.editorCard}>
        <textarea
          className={tc.aiPrompt}
          style={{ border: 'none', background: 'transparent', minHeight: 92 }}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          disabled={submitting}
          placeholder="What good looks like, links, context for whoever picks this up… ({{merge.fields}} resolve against the linked record)"
        />
      </div>

      {assigneeId != null && currentUserId != null && assigneeId !== currentUserId && assignee && (
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-faint)' }}>
          {firstName(assignee.name)} will see “from you” on it.
        </div>
      )}

      {error && (
        <div className={`${tc.ruleCard} ${tc.ruleCardWarn}`} style={{ marginTop: 12, marginBottom: 0 }}>
          <span className={tc.ruleBlocker}>{error}</span>
        </div>
      )}
    </DetailModalShell>,
    document.body,
  );
};

export default TodoCreateContainer;
